import './DataManagementTab.css';
import { apiClient } from '../../services/apiClient';

export default function DataManagementTab({ isLoading }) {

  const handleExportConversations = async () => {
    try {
      // Show loading state
      const exportButton = document.querySelector('.export-button');
      exportButton.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Exporting...';
      exportButton.disabled = true;

      // Get all chat sessions from the database using API client
      const { sessions } = await apiClient.getChatSessions();
      
      if (!sessions || sessions.length === 0) {
        alert('No conversations found to export.');
        return;
      }

      // Get messages for each session
      const conversationsWithMessages = [];
      
      for (const session of sessions) {
        try {
          const sessionId = session.sessionId || session.id;
          const { messages } = await apiClient.getSessionMessages(sessionId);
          
            conversationsWithMessages.push({
            sessionId: sessionId,
              title: session.title || 'Untitled',
              createdAt: session.createdAt || session.created_at,
              updatedAt: session.updatedAt || session.updated_at,
              messageCount: messages ? messages.length : 0,
              messages: messages || []
            });
        } catch (error) {
          console.warn(`Failed to fetch messages for session ${session.sessionId || session.id}:`, error);
          // Add session without messages
          conversationsWithMessages.push({
            sessionId: session.sessionId || session.id,
            title: session.title || 'Untitled',
            createdAt: session.createdAt || session.created_at,
            updatedAt: session.updatedAt || session.updated_at,
            messageCount: 0,
            messages: []
          });
        }
      }

      // Create export data
      const exportData = {
        exportDate: new Date().toISOString(),
        totalConversations: conversationsWithMessages.length,
        totalMessages: conversationsWithMessages.reduce((sum, conv) => sum + conv.messageCount, 0),
        conversations: conversationsWithMessages
      };

      // Create and download file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `conversations_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      // Show success message
      alert(`Successfully exported ${conversationsWithMessages.length} conversations with ${exportData.totalMessages} total messages!`);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export conversations. Please try again.');
    } finally {
      // Restore button state
      const exportButton = document.querySelector('.export-button');
      if (exportButton) {
        exportButton.innerHTML = '<i class="fas fa-download"></i> Export Conversations';
        exportButton.disabled = false;
      }
    }
  };

  const handleDeleteAllData = async () => {
    // Double confirmation for destructive action
    const firstConfirm = confirm('⚠️ WARNING: This will permanently delete YOUR PERSONAL CHAT DATA:\n\n• All your chat conversations\n• All your chat messages\n\n❗ This will NOT delete:\n• Organization context or knowledge base\n• Shared memories or documents\n• Other users\' data\n\nThis action CANNOT be undone!\n\nAre you sure you want to continue?');
    
    if (!firstConfirm) return;
    
    const secondConfirm = confirm('🔴 FINAL CONFIRMATION\n\nYou are about to permanently delete all YOUR personal chat conversations.\n\nThis will NOT affect organization data.\n\nAre you absolutely sure?');
    
    if (!secondConfirm) return;

    try {
      // Show loading state
      const deleteButton = document.querySelector('.delete-all-button');
      deleteButton.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Deleting...';
      deleteButton.disabled = true;

      // Delete all data from backend using API client
      const result = await apiClient.deleteAllData();
      
      // Note: Chat sessions are stored in backend database, not localStorage
      // Only clear user preferences if needed
      localStorage.removeItem('userSettings');
      
      // Show success message with details
      alert(`✅ Your personal chat data deleted successfully!\n\nDeleted:\n• ${result.deleted_sessions || 0} chat conversations\n• ${result.deleted_messages || 0} messages\n\n✓ Organization context and knowledge base remain intact`);
      
      // Refresh the page to reset the app state
      window.location.reload();
      
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete chat data. Please try again or contact support.');
    } finally {
      // Restore button state (in case of error)
      const deleteButton = document.querySelector('.delete-all-button');
      if (deleteButton) {
        deleteButton.innerHTML = '<i class="fas fa-trash-alt"></i> Delete My Chats';
        deleteButton.disabled = false;
      }
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading data management...</p>
      </div>
    );
  }

  return (
    <div className="data-management-tab">
      <div className="tab-header">
        <h2>Data Management</h2>
        <p>Export your conversation data or permanently delete all stored information</p>
      </div>

      <div className="data-management-sections">
        
        {/* Export Data Section */}
        <div className="data-section">
          <div className="section-header">
            <h3>
              <i className="fas fa-download"></i>
              Export Data
            </h3>
          </div>
          <p className="section-description">
            Download all your conversation data as a JSON file for backup or transfer purposes.
          </p>
          <div className="section-actions">
            <button
              className="btn btn-primary export-button"
              onClick={handleExportConversations}
              title="Export all conversations as JSON"
            >
              <i className="fas fa-download"></i>
              Export Conversations
            </button>
          </div>
        </div>

        {/* Delete Data Section */}
        <div className="data-section danger-section">
          <div className="section-header">
            <h3>
              <i className="fas fa-exclamation-triangle"></i>
              Danger Zone
            </h3>
          </div>
          <p className="section-description">
            Permanently delete all YOUR personal chat conversations and messages. This action cannot be undone. 
            <strong> This will NOT delete organization context, knowledge base, or shared data.</strong>
          </p>
          <div className="section-actions">
            <button
              className="btn btn-danger delete-all-button"
              onClick={handleDeleteAllData}
              title="Permanently delete all your personal chat conversations and messages"
            >
              <i className="fas fa-trash-alt"></i>
              Delete My Chats
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
